// 영웅 선택 저장(c022, dungeonhero.hero.v1): 지금 플레이하는 영웅 하나. 기존 키(growth.v2·skillslots.v1·profile.v2·gear.v1·save.v1·stage.v1)는 건드리지 않는다.
// 저장이 없으면 카엘(기존 사용자 영향 0). 손상은 원문을 .bak에 한 번 남기고 카엘로 시작한다(다음 선택 때 덮어쓴다).
// 미래 버전은 읽을 수 있는 영웅 값이면 그대로 쓰되 덮어쓰지 않는다(새 빌드의 다른 필드를 지우지 않게). 읽기 예외는 메모리만.
// 쓰기는 고정 스텝 밖 flush()에서만 한다(슬롯 배치와 같은 규칙: 틱 안 직렬화·동기 쓰기 금지).
import { PLAYABLE_HEROES, type PlayableHeroId } from "../shared/types";
import type { SaveStorage } from "./save";

export const HERO_SELECT_KEY = "dungeonhero.hero.v1";
export const HERO_SELECT_VERSION = 1;
export type HeroSelectMode = "ok" | "future" | "unavailable";

const known = (v: unknown): v is PlayableHeroId => typeof v === "string" && (PLAYABLE_HEROES as readonly string[]).includes(v);

export function serializeHeroSelect(hero: PlayableHeroId): string {
  return JSON.stringify({ version: HERO_SELECT_VERSION, hero });
}
/** 원문 → 영웅. corrupt = 형식이 틀림, future = 더 높은 version(hero는 읽을 수 있으면 함께 준다) */
export function parseHeroSelect(raw: string): { ok: true; hero: PlayableHeroId } | { ok: false; reason: "corrupt" } | { ok: false; reason: "future"; hero: PlayableHeroId | null } {
  let v: unknown;
  try { v = JSON.parse(raw); } catch { return { ok: false, reason: "corrupt" }; }
  if (v === null || typeof v !== "object" || Array.isArray(v)) return { ok: false, reason: "corrupt" };
  const o = v as Record<string, unknown>;
  if (typeof o.version === "number" && o.version > HERO_SELECT_VERSION) return { ok: false, reason: "future", hero: known(o.hero) ? o.hero : null };
  if (o.version !== HERO_SELECT_VERSION || !known(o.hero)) return { ok: false, reason: "corrupt" };
  return { ok: true, hero: o.hero };
}

export class HeroSelectStore {
  hero: PlayableHeroId = "kael";
  mode: HeroSelectMode = "ok";
  lastSaveOk = true;
  private due = false;
  /** 메모리와 같은 영웅을 다시 골랐다: 다른 탭이 저장값을 바꿨을 수 있어 flush에서 저장값을 확인한다 */
  private recheck = false;

  constructor(private readonly storage: SaveStorage) {
    let raw: string | null;
    try { raw = storage.getItem(HERO_SELECT_KEY); } catch { this.mode = "unavailable"; return; }
    if (raw === null) return;
    const p = parseHeroSelect(raw);
    if (p.ok) { this.hero = p.hero; return; }
    if (p.reason === "future") { this.mode = "future"; if (p.hero) this.hero = p.hero; return; }
    try { if (storage.getItem(HERO_SELECT_KEY + ".bak") === null) storage.setItem(HERO_SELECT_KEY + ".bak", raw); } catch { /* 백업 실패는 진행을 막지 않는다 */ }
  }

  /** 메모리에 바로 반영하고 저장은 flush()로 미룬다. 바뀌었으면 true */
  set(hero: PlayableHeroId): boolean {
    if (hero === this.hero) { this.recheck = true; return false; }
    this.hero = hero;
    this.due = true;
    return true;
  }

  /** 고정 스텝 밖(렌더 프레임·pagehide)에서 부른다. 미래 버전·읽기 불가 저장은 덮어쓰지 않는다 */
  flush(): void {
    if ((!this.due && !this.recheck) || this.mode !== "ok") return;
    if (!this.due) {
      // 같은 영웅 재선택: 저장값이 이 탭의 영웅과 다르면(다른 탭이 바꿨다) 이 선택을 저장한다
      this.recheck = false;
      let raw: string | null;
      try { raw = this.storage.getItem(HERO_SELECT_KEY); } catch { this.lastSaveOk = false; return; }
      if (raw === null) { if (this.hero === "kael") return; }
      else {
        const p = parseHeroSelect(raw);
        if (p.ok && p.hero === this.hero) return;
        if (!p.ok && p.reason === "future") return;
      }
      this.due = true;
    }
    try { this.storage.setItem(HERO_SELECT_KEY, serializeHeroSelect(this.hero)); this.due = false; this.lastSaveOk = true; }
    catch { this.lastSaveOk = false; }
  }
}
